import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuthStore, useEntityStore } from '../store';
import { authAPI, entitiesAPI } from '../lib/api';
import { toast } from 'sonner';

function GoogleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

export function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Check for error from callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    if (errorParam) {
      const messages = {
        invalid_state: 'Authentication session expired. Please try again.',
        token_exchange_failed: 'Google sign-in failed. Please try again.',
        userinfo_failed: 'Could not retrieve your Google account info.',
        no_email: 'Your Google account has no email address.',
        missing_params: 'Authentication failed. Please try again.',
        unauthorized_email: 'Your account is not authorized to access this application.',
      };
      setError(messages[errorParam] || `Sign-in error: ${errorParam}`);
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError('');
    try {
      const { data } = await authAPI.googleLogin();
      const authUrl = new URL(data.auth_url);
      if (!authUrl.hostname.endsWith('google.com') && !authUrl.hostname.endsWith('googleapis.com')) {
        throw new Error('Invalid OAuth URL');
      }
      window.location.href = data.auth_url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initiate Google sign-in');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-primary/5">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4">
            <span className="text-primary-foreground font-bold text-lg">FA</span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Finance Agent</h1>
          <p className="text-muted-foreground mt-2">Multi-entity finance management</p>
        </div>

        <Card className="border-border/50 shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl">Sign in</CardTitle>
            <CardDescription>Use your Google account to access Finance Agent</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Button
              onClick={handleGoogleSignIn}
              className="w-full h-11"
              disabled={isLoading}
              variant="outline"
              data-testid="google-sign-in-btn"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <GoogleIcon className="h-5 w-5 mr-2" />
                  Sign in with Google
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const { setEntities, setSelectedEntity, setSelectedScope } = useEntityStore();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');

    if (error) {
      toast.error('Google sign-in failed');
      navigate('/login');
      return;
    }

    if (!code) {
      navigate('/login');
      return;
    }

    (async () => {
      try {
        const { data } = await authAPI.googleExchange(code);
        setAuth(data.access_token, data.refresh_token, data.user);

        const entitiesRes = await entitiesAPI.getAll();
        setEntities(entitiesRes.data);
        // Land on the Overall group-wide dashboard.
        setSelectedScope('overall');
        setSelectedEntity('all');

        toast.success('Welcome!');
        navigate('/overall');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Sign-in failed');
        navigate('/login');
      }
    })();
  }, [navigate, setAuth, setEntities, setSelectedEntity, setSelectedScope]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <span className="ml-3 text-muted-foreground">Signing in...</span>
    </div>
  );
}
